import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children }) => (
  <div style={eyebrowStyle} className="mb-6">
    {children}
  </div>
);

const Weddings = () => {
  const t = useT();
  const subNavLinks = [
    { label: t('subnav.approach'), target: 'approach' },
    { label: t('subnav.method'), target: 'method' },
    { label: t('subnav.capabilities'), target: 'capabilities' },
    { label: t('subnav.ceremonies'), target: 'ceremonies' },
    { label: t('subnav.studio'), target: 'studio' },
    { label: t('subnav.contact'), target: 'contact' },
  ];

  const method = ['01', '02', '03', '04', '05'].map((no) => ({
    no,
    title: t(`weddings.method.${no}.title`),
    desc: t(`weddings.method.${no}.desc`),
  }));

  const capabilities = [
    { titleKey: 'weddings.capabilities.venues', lines: t('weddings.capabilities.venuesLines') || [] },
    { titleKey: 'weddings.capabilities.ritual', lines: t('weddings.capabilities.ritualLines') || [] },
    { titleKey: 'weddings.capabilities.guest', lines: t('weddings.capabilities.guestLines') || [] },
  ];

  const ceremonyMedia = [
    {
      venue: 'ÇIRAĞAN PALACE KEMPINSKI',
      image: 'https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=1200&q=80',
    },
    {
      venue: 'ARGOS IN CAPPADOCIA',
      image: 'https://images.unsplash.com/photo-1495344517868-8ebaf0a2044a?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80',
    },
    {
      venue: 'LIYA ISLAND',
      image: 'https://images.unsplash.com/photo-1525772764200-be829a350797?w=1200&q=80',
      fallback: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=80',
    },
  ];
  const ceremonyCopy = t('weddings.ceremonies.items') || [];
  const ceremonies = ceremonyMedia.map((m, i) => ({ ...m, ...(ceremonyCopy[i] || {}) }));

  return (
    <div
      className="min-h-screen text-bone"
      style={{
        background:
          'linear-gradient(180deg, #0E1B3F 0%, #0A1130 50%, #060B22 100%)',
      }}
      data-testid="weddings-page"
    >
      <SEO
        title="Weddings, Destination Ceremonies in Türkiye | GlowMeets"
        description="Destination weddings across Türkiye, Bosphorus palaces, Cappadocia valleys, Aegean cliffs. Designed end-to-end by one studio with one operations team."
        keywords="destination wedding Turkey, Bosphorus wedding, Cappadocia wedding, Çırağan Palace wedding, Kempinski wedding Istanbul, Aegean elopement Turkey"
        url="/weddings"
        service={{
          name: 'GlowMeets Weddings',
          serviceType: 'Destination weddings and multi-day celebrations',
          description:
            'Destination weddings across Türkiye, multi-day arcs from welcome dinner to brunch, by one studio with one operations team.',
        }}
      />

      {/* ───────────────────────── 1. SUB-HERO ───────────────────────── */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: '88vh' }}
        data-testid="weddings-subhero"
      >
        <img
          src="https://images.unsplash.com/photo-1519741497674-611481863552?w=2200&q=80"
          alt="Bosphorus wedding ceremony at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Horizontal duotone fade, graphite navy left, photo on right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(14,27,63,0.94) 0%, rgba(14,27,63,0.86) 38%, rgba(14,27,63,0.5) 65%, rgba(14,27,63,0.18) 100%)',
          }}
        />
        {/* Soft bottom fade into the next section */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: '180px',
            background:
              'linear-gradient(to bottom, rgba(14,27,63,0) 0%, #0E1B3F 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{
            minHeight: '88vh',
            paddingTop: '120px',
            paddingBottom: '90px',
          }}
        >
          <div className="max-w-[58ch]">
            <SectionEyebrow>{t('weddings.hero.eyebrow')}</SectionEyebrow>

            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(40px, 5vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              <span className="block">{t('weddings.hero.line1')}</span>
              <span
                className="block italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('weddings.hero.line2')}
              </span>
              <span
                className="block italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('weddings.hero.line3')}
              </span>
            </h1>

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(244,239,227,0.82)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {t('weddings.hero.sub')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav (navy surface to match the page) */}
      <EditorialSubNav links={subNavLinks} surface="navy" />

      {/* ───────────────────────── 2. THE APPROACH ───────────────────────── */}
      <section
        id="approach"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="weddings-approach"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[60ch]">
            <SectionEyebrow>{t('weddings.approach.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(28px, 2.8vw, 36pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {t('weddings.approach.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('weddings.approach.headline2')}
              </em>
            </h2>

            <div
              className="mt-10 space-y-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: 'rgba(244,239,227,0.82)',
              }}
            >
              {(t('weddings.approach.body') || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div
              className="mt-12"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 3. THE METHOD ───────────────────────── */}
      <section
        id="method"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="weddings-method"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <SectionEyebrow>{t('weddings.method.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
              }}
            >
              {t('weddings.method.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('weddings.method.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            {method.map((step, i) => (
              <div
                key={step.no}
                className="relative px-6 py-8"
                style={{
                  borderTop: '1px solid rgba(214,168,122,0.45)',
                  borderRight:
                    i < method.length - 1
                      ? '1px solid rgba(214,168,122,0.18)'
                      : 'none',
                }}
                data-testid={`weddings-method-${step.no}`}
              >
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '64pt',
                    lineHeight: 1,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: '#D6A87A',
                  }}
                >
                  {step.no}
                </div>

                <div
                  className="mt-6"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                  }}
                >
                  {step.title}
                </div>

                <p
                  className="mt-3"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(244,239,227,0.82)',
                  }}
                >
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. CAPABILITIES ───────────────────────── */}
      <section
        id="capabilities"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="weddings-capabilities"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionEyebrow>{t('weddings.capabilities.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontSize: '28pt',
                lineHeight: 1.1,
                fontWeight: 400,
                color: '#D6A87A',
                letterSpacing: '-0.005em',
              }}
            >
              {t('weddings.capabilities.headline')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {capabilities.map((c, idx) => (
              <div
                key={c.titleKey}
                data-testid={`weddings-capability-${idx + 1}`}
              >
                <div
                  style={{
                    height: '1px',
                    width: '40px',
                    backgroundColor: 'rgba(214,168,122,0.55)',
                  }}
                  className="mb-5"
                />
                <h3
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '18pt',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    color: '#D6A87A',
                    letterSpacing: '-0.005em',
                  }}
                  className="mb-5"
                >
                  {t(c.titleKey)}
                </h3>
                <ul className="space-y-3">
                  {c.lines.map((line) => (
                    <li
                      key={line}
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '10pt',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        color: 'rgba(244,239,227,0.82)',
                      }}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 5. SIGNATURE CEREMONIES ───────────────────────── */}
      <section
        id="ceremonies"
        style={{ paddingTop: '0', paddingBottom: '90px' }}
        data-testid="weddings-ceremonies"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow>{t('weddings.ceremonies.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
              }}
            >
              {t('weddings.ceremonies.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('weddings.ceremonies.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {ceremonies.map((c, index) => (
              <article
                key={c.title}
                className="group"
                data-testid={`weddings-case-${index + 1}`}
              >
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: '4/5' }}
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    onError={(e) => {
                      if (c.fallback && e.currentTarget.src !== c.fallback) {
                        e.currentTarget.src = c.fallback;
                      }
                    }}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  />
                  {/* Navy bottom overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, rgba(10,19,47,0) 60%, rgba(10,19,47,0.85) 100%)',
                    }}
                  />
                  {/* Copper category chip */}
                  <div
                    className="absolute top-5 left-5"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      border: '1px solid rgba(214,168,122,0.6)',
                      padding: '6px 12px',
                      backgroundColor: 'rgba(10,19,47,0.45)',
                    }}
                  >
                    {t('filters.weddings')}
                  </div>
                </div>

                <div className="mt-6">
                  <div
                    className="mb-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: 'rgba(214,168,122,0.78)',
                    }}
                  >
                    {c.kind} · {c.venue}
                  </div>

                  <h3
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '22pt',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: '#D6A87A',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {c.title}
                  </h3>

                  <p
                    className="mt-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '10pt',
                      fontWeight: 400,
                      lineHeight: 1.65,
                      color: 'rgba(244,239,227,0.82)',
                    }}
                  >
                    {c.summary}
                  </p>

                  <div
                    className="mt-5"
                    style={{
                      height: '1px',
                      width: '32px',
                      backgroundColor: 'rgba(214,168,122,0.55)',
                    }}
                  />

                  <div
                    className="mt-4"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                    }}
                  >
                    {t('common.outcome')}, {c.outcome}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 6. THE STUDIO (bone band, break out for warmth) ───────────────────────── */}
      <section
        id="studio"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="weddings-studio"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch] mx-auto">
            <SectionEyebrow>{t('weddings.studio.eyebrow')}</SectionEyebrow>

            <p
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '16pt',
                lineHeight: 1.55,
                color: '#0A132F',
                letterSpacing: '-0.002em',
              }}
            >
              {t('weddings.studio.body')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 7. SECTION CTA ───────────────────────── */}
      <section
        id="contact"
        className="relative overflow-hidden"
        style={{
          paddingTop: '90px',
          paddingBottom: '90px',
          background:
            'linear-gradient(180deg, #0E1B3F 0%, #060B22 100%)',
        }}
        data-testid="weddings-cta"
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2"
          style={{
            height: '1px',
            width: '160px',
            backgroundColor: '#D6A87A',
          }}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionEyebrow>{t('weddings.cta.eyebrow')}</SectionEyebrow>

          <h2
            className="text-bone"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(36px, 4vw, 54px)',
              lineHeight: 1.05,
              fontWeight: 500,
            }}
          >
            {t('weddings.cta.headline1')}{' '}
            <em
              className="italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('weddings.cta.headline2')}
            </em>
          </h2>

          <div className="mt-12 flex flex-col items-center gap-5">
            <Link
              to="/contact"
              data-testid="weddings-cta-primary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#0A132F',
                backgroundColor: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B98556';
                e.currentTarget.style.borderColor = '#B98556';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.borderColor = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')}
            </Link>

            <Link
              to="/weddings/capabilities"
              data-testid="weddings-cta-secondary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.downloadWeddingsLookbook')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Weddings;
